import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, ShieldCheck, BookOpen, Briefcase } from "lucide-react";
import { Biz360Logo } from "../components/layout/Biz360Logo";
import { PublicFooter } from "../components/layout/PublicFooter";

export const StatutAutoEntrepreneurPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Biz360Logo />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={16} /> Retour
          </button>
        </div>
      </header>

      <main
        className="flex-1 max-w-3xl w-full mx-auto px-6 py-10"
        data-testid="statut-ae-page"
      >
        <h1 className="text-3xl font-bold mb-2" style={{ color: "#210875" }}>
          L'essentiel du statut d'auto-entrepreneur
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Tout ce qu'il faut savoir avant de se lancer — et tout au long de son activité.
        </p>

        {/* Bloc lien officiel Urssaf */}
        <div
          className="p-5 rounded-xl mb-10 border-2"
          style={{ backgroundColor: "#FDFCFF", borderColor: "#210875" }}
          data-testid="urssaf-official-link"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck size={28} style={{ color: "#210875" }} className="shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold mb-2" style={{ color: "#210875" }}>
                Source officielle de référence
              </h2>
              <p className="text-sm mb-3" style={{ color: "#210875" }}>
                Cette page propose une synthèse pédagogique du statut. Pour des informations
                complètes, à jour et faisant autorité, nous renvoyons systématiquement au site
                officiel de l'Urssaf dédié aux auto-entrepreneurs :
              </p>
              <a
                href="https://www.autoentrepreneur.urssaf.fr/portail/accueil.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition hover:opacity-90"
                style={{ backgroundColor: "#210875", color: "white" }}
                data-testid="urssaf-portal-cta"
              >
                <ExternalLink size={16} />
                L'essentiel du statut — autoentrepreneur.urssaf.fr
              </a>
            </div>
          </div>
        </div>

        {/* Section 1 — Création */}
        <Section
          id="creation"
          title="1. Création du statut d'auto-entrepreneur"
          icon={<BookOpen size={20} style={{ color: "#897FCB" }} />}
        >
          <p>
            L'auto-entreprise (officiellement <em>micro-entreprise</em>) est un régime simplifié
            de l'entreprise individuelle créé par la loi de modernisation de l'économie du 4
            août 2008 (« LME »). Il permet de débuter une activité indépendante avec des
            formalités allégées et un calcul forfaitaire des charges sociales et fiscales.
          </p>
          <p>
            <strong>Démarches de création :</strong> elles s'effectuent intégralement en ligne et
            gratuitement via le Guichet Unique des formalités d'entreprises :
          </p>
          <ul>
            <li>
              <a
                href="https://formalites.entreprises.gouv.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: "#210875" }}
              >
                formalites.entreprises.gouv.fr
              </a>{" "}
              (Guichet Unique INPI — obligatoire depuis le 1ᵉʳ janvier 2023)
            </li>
            <li>
              Pour la déclaration d'activité spécifique Urssaf :{" "}
              <a
                href="https://www.autoentrepreneur.urssaf.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: "#210875" }}
              >
                autoentrepreneur.urssaf.fr
              </a>
            </li>
          </ul>
          <p>
            À l'issue des démarches, l'auto-entrepreneur reçoit un <strong>numéro SIRET</strong>{" "}
            (Système d'Identification du Répertoire des Établissements) qui officialise
            l'existence juridique de son activité.
          </p>
        </Section>

        {/* Section 2 — Spécificités du statut */}
        <Section
          id="specificites"
          title="2. Spécificités du statut"
          icon={<BookOpen size={20} style={{ color: "#897FCB" }} />}
        >
          <p>
            <strong>Plafonds de chiffre d'affaires (2025-2026) :</strong> le statut
            d'auto-entrepreneur est soumis à des plafonds annuels de CA HT, au-delà desquels il
            n'est plus applicable :
          </p>
          <ul>
            <li>
              <strong>203 100 €/an</strong> pour les activités de vente de marchandises,
              fourniture de denrées à emporter/consommer sur place et fourniture de logement
            </li>
            <li>
              <strong>83 600 €/an</strong> pour les prestations de services (BIC et BNC) et les
              activités libérales
            </li>
          </ul>
          <p className="text-xs italic mt-1">
            ⚠️ Plafonds <strong>2026</strong> revalorisés au 1ᵉʳ janvier 2026. En activité mixte
            (ventes + services), le plafond global est de 203 100 € avec un maximum de 83 600 €
            pour la part services.
          </p>
          <p>
            <strong>Franchise en base de TVA :</strong> tant que le CA reste sous certains
            seuils, l'auto-entrepreneur n'est pas redevable de la TVA (article 293 B du Code
            général des impôts). Au-delà, il devient assujetti et doit la collecter sur ses
            ventes. Les seuils 2025-2026 sont de 37 500 € (services, base) / 41 250 € (services,
            majoré) et 85 000 € (ventes, base) / 93 500 € (ventes, majoré).
          </p>
          <p>
            <strong>Régime micro-social et micro-fiscal :</strong> les cotisations sociales et
            l'impôt sur le revenu (sur option, via le <em>versement libératoire</em>) sont
            calculés en pourcentage du CA déclaré, sans déduction de frais réels. Le calcul est
            réalisé automatiquement à chaque déclaration (mensuelle ou trimestrielle).
          </p>
          <p>
            <strong>Comptabilité simplifiée :</strong> obligation de tenir un livre des recettes
            (et un registre des achats pour les activités d'achat-revente). L'auto-entrepreneur
            est dispensé du dépôt des comptes annuels.
          </p>
          <p>
            <strong>Aides à la création :</strong> les auto-entrepreneurs créateurs peuvent
            bénéficier de dispositifs comme <strong>l'ACRE</strong> (Aide aux Créateurs et
            Repreneurs d'Entreprise — exonération partielle de cotisations la première année) ou
            l'ARCE (capitalisation d'une partie de l'ARE pour les demandeurs d'emploi).
          </p>
        </Section>

        {/* Section 3 — Cotisations obligatoires */}
        <Section
          id="cotisations"
          title="3. Les cotisations sociales : un caractère obligatoire"
          icon={<ShieldCheck size={20} style={{ color: "#897FCB" }} />}
        >
          <div
            className="p-3 rounded-lg mb-3 text-sm font-medium border"
            style={{ backgroundColor: "#FEF3C7", borderColor: "#F59E0B", color: "#92400E" }}
          >
            ⚠️ <strong>Le paiement des cotisations sociales est obligatoire pour tout
            auto-entrepreneur dès le premier euro de chiffre d'affaires encaissé.</strong> Le
            défaut de déclaration ou de paiement expose à des majorations de retard, des
            pénalités, voire à la radiation du régime micro-social.
          </div>
          <p>
            <strong>Déclaration et paiement :</strong> l'auto-entrepreneur doit déclarer son
            chiffre d'affaires <strong>tous les mois</strong> ou <strong>tous les trimestres
            </strong> (au choix lors de la création), même en l'absence d'activité (« déclaration
            à zéro » obligatoire). Le paiement est automatique par prélèvement.
          </p>
          <p>
            <strong>Taux 2025-2026 (hors ACRE et options spécifiques) :</strong>
          </p>
          <ul>
            <li>Vente de marchandises et fourniture de logement (BIC) : <strong>12,3 %</strong></li>
            <li>Prestations de services commerciales et artisanales (BIC) : <strong>21,2 %</strong></li>
            <li>Prestations de services libérales (BNC) — CIPAV : <strong>23,2 %</strong></li>
            <li>Prestations de services libérales — Régime général SSI (BNC) : <strong>25,6 %</strong></li>
            <li>Location meublée de tourisme classée : <strong>6 %</strong></li>
          </ul>
          <p>
            Ces taux correspondent aux <strong>cotisations sociales principales</strong>. La
            <strong> contribution à la formation professionnelle (CFP)</strong> est prélevée en
            supplément selon l'activité exercée. Le calcul est forfaitaire :{" "}
            <strong>taux × CA HT déclaré</strong>.
          </p>
          <p className="text-xs italic">
            ⚠️ Taux applicables en 2026, susceptibles d'évolution réglementaire en cours d'année.
            Pour les barèmes officiels à jour, consultez{" "}
            <a
              href="https://www.autoentrepreneur.urssaf.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: "#210875" }}
            >
              autoentrepreneur.urssaf.fr
            </a>
            .
          </p>
        </Section>

        {/* Section 4 — Rôle des cotisations */}
        <Section
          id="role-cotisations"
          title="4. À quoi servent vos cotisations ? (rôle des cotisations)"
          icon={<ShieldCheck size={20} style={{ color: "#897FCB" }} />}
        >
          <p>
            Les cotisations sociales versées par l'auto-entrepreneur financent l'ensemble de sa
            protection sociale et celle de tous les actifs en France. Elles ouvrent des droits
            concrets :
          </p>
          <ul>
            <li>
              <strong>🏥 Assurance maladie-maternité :</strong> remboursement des frais de santé,
              indemnités journalières en cas d'arrêt maladie ou de congé maternité/paternité
            </li>
            <li>
              <strong>👴 Retraite de base :</strong> validation de trimestres de retraite (4
              trimestres/an si le CA dépasse les seuils minimaux fixés par décret)
            </li>
            <li>
              <strong>📈 Retraite complémentaire :</strong> acquisition de points de retraite
              complémentaire obligatoire (RCI ou CIPAV selon l'activité)
            </li>
            <li>
              <strong>♿ Invalidité-décès :</strong> versement d'une pension en cas d'invalidité
              ou capital décès aux ayants droit
            </li>
            <li>
              <strong>👨‍👩‍👧 Allocations familiales :</strong> contribution au financement des
              prestations familiales (CAF)
            </li>
            <li>
              <strong>🎓 Contribution à la Formation Professionnelle (CFP) :</strong> contribue
              au financement de la formation professionnelle et peut permettre l'acquisition de
              droits à la formation selon les règles en vigueur (le Compte Personnel de
              Formation n'est pas alimenté automatiquement dans tous les cas si certains seuils
              de revenus ne sont pas atteints).
            </li>
            <li>
              <strong>🤝 CSG/CRDS :</strong> contributions de solidarité au financement de la
              Sécurité sociale et au remboursement de la dette sociale
            </li>
          </ul>
          <p className="text-sm italic">
            Les cotisations financent la protection sociale obligatoire (maladie, retraite,
            invalidité, formation professionnelle, prestations familiales, etc.) et ouvrent
            des droits selon les règles applicables à chaque régime.
          </p>
        </Section>

        {/* Section 5 — Droit du travail */}
        <Section
          id="droit-travail"
          title="5. Auto-entrepreneur et droit du travail"
          icon={<Briefcase size={20} style={{ color: "#897FCB" }} />}
        >
          <p>
            <strong>Statut de travailleur indépendant :</strong> l'auto-entrepreneur n'est{" "}
            <strong>pas un salarié</strong>. Il exerce son activité en toute indépendance, à son
            propre compte, et n'est lié à ses clients que par des contrats de prestation de
            services ou de vente — jamais par un contrat de travail. À ce titre, il ne bénéficie
            ni de la durée légale du travail, ni des congés payés, ni de l'assurance chômage
            classique des salariés.
          </p>
          <p>
            <strong>Risque de requalification (salariat déguisé) :</strong> si un
            auto-entrepreneur travaille pour un client unique de manière régulière, dans un lien
            de subordination juridique (horaires imposés, ordres directs, intégration à
            l'organisation du client), la relation peut être requalifiée par les Prud'hommes ou
            l'Urssaf en contrat de travail salarié. Le client encourt alors des sanctions
            (rappel de cotisations, dommages-intérêts, voire poursuites pour travail dissimulé —
            articles L8221-1 et suivants du Code du travail).
          </p>
          <p>
            <strong>Cumul avec un emploi salarié :</strong> il est possible d'être à la fois
            salarié et auto-entrepreneur, sous réserve de respecter une éventuelle clause
            d'exclusivité ou de non-concurrence du contrat de travail, et d'informer son
            employeur le cas échéant.
          </p>
          <p>
            <strong>Si l'auto-entrepreneur emploie des salariés :</strong> l'embauche de salariés
            est juridiquement possible, mais en pratique limitée par les plafonds de CA du
            régime micro. L'employeur auto-entrepreneur est alors soumis à l'intégralité du
            droit du travail : contrat écrit, salaire minimum (SMIC), durée légale du travail,
            congés payés, déclaration préalable à l'embauche (DPAE), affichage obligatoire,
            visite médicale, médecine du travail, etc.
          </p>
          <p>
            <strong>Pour aller plus loin :</strong> consultez les ressources officielles sur le
            droit du travail :
          </p>
          <ul>
            <li>
              <a
                href="https://travail-emploi.gouv.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: "#210875" }}
              >
                Ministère du Travail — travail-emploi.gouv.fr
              </a>
            </li>
            <li>
              <a
                href="https://www.service-public.fr/particuliers/vosdroits/N19806"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: "#210875" }}
              >
                Service-public.fr — Travail indépendant et droits
              </a>
            </li>
            <li>
              <a
                href="https://code.travail.gouv.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: "#210875" }}
              >
                Code du Travail Numérique
              </a>
            </li>
          </ul>
        </Section>

        {/* Bloc final Urssaf */}
        <div
          className="mt-12 p-5 rounded-xl border text-center"
          style={{ backgroundColor: "#F8BC9120", borderColor: "#F8BC91" }}
        >
          <p className="text-sm font-medium mb-3" style={{ color: "#210875" }}>
            Cette page est une synthèse pédagogique non-exhaustive. Pour les informations
            officielles, complètes et à jour :
          </p>
          <a
            href="https://www.autoentrepreneur.urssaf.fr/portail/accueil.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition hover:opacity-90"
            style={{ backgroundColor: "#210875", color: "white" }}
            data-testid="urssaf-portal-cta-bottom"
          >
            <ExternalLink size={16} />
            Visiter autoentrepreneur.urssaf.fr
          </a>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

const Section = ({ id, title, icon, children }) => (
  <section id={id} className="mb-10 scroll-mt-24" data-testid={`section-${id}`}>
    <h3
      className="text-xl font-bold mb-4 flex items-center gap-2"
      style={{ color: "#210875" }}
    >
      {icon}
      {title}
    </h3>
    <div className="text-sm text-muted-foreground space-y-3 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5">
      {children}
    </div>
  </section>
);

export default StatutAutoEntrepreneurPage;
